import { createContext, type PropsWithChildren, useContext } from 'react';

import { type UsePaginatedSelectionReturnType } from './usePaginatedSelection.types';

const PaginatedSelectionContext =
  createContext<UsePaginatedSelectionReturnType | null>(null);

interface DataTablePaginatedSelectionProviderProps extends PropsWithChildren {
  value: UsePaginatedSelectionReturnType;
}

/**
 * **DO NOT** call `usePaginatedSelection` directly in the table body as this will cause
 * the entire table to re-render on every selection change. Instead, declare `usePaginatedSelection`
 * with `<PaginatedSelectionContextProvider />` in a wrapper component that then wraps the table
 * component. This allows the main table to skip re-renders when selection state changes, ensuring
 * that checkboxes remain snappy with large/complex tables.
 */
export const PaginatedSelectionContextProvider = ({
  value,
  children,
}: DataTablePaginatedSelectionProviderProps) => (
  <PaginatedSelectionContext.Provider value={value}>
    {children}
  </PaginatedSelectionContext.Provider>
);

export const usePaginatedSelectionContext = () => {
  const context = useContext(PaginatedSelectionContext);

  if (!context) {
    throw new Error(
      'usePaginatedSelectionContext must be used within `<PaginatedSelectionContextProvider />`',
    );
  }

  return context;
};
